<template>
  <div class="inline-flex items-center gap-1.5" :class="interactive ? 'cursor-pointer select-none' : ''">
    <!-- Star Icons -->
    <div class="flex items-center" :class="starSpacing">
      <button
        v-for="index in max"
        :key="index"
        type="button"
        :disabled="!interactive"
        @mouseenter="onHover(index)"
        @mouseleave="onLeave"
        @click="onClick(index)"
        :class="[
          'transition-all duration-150 focus:outline-none',
          interactive ? 'hover:scale-125 active:scale-95 cursor-pointer' : 'cursor-default pointer-events-none'
        ]"
        :title="interactive ? `Rate ${index} of ${max}` : `${currentRating} out of ${max}`"
      >
        <svg
          :class="[starSize, getStarColor(index)]"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          fill="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
          />
        </svg>
      </button>
    </div>

    <!-- Rating Numeric Label -->
    <span
      v-if="showValue"
      :class="['font-extrabold', textSize, textColor]"
    >
      {{ displayRating }}
    </span>

    <!-- Total Review Count -->
    <span
      v-if="totalCount !== null && totalCount !== undefined"
      :class="['font-medium text-slate-400 dark:text-slate-500', countSize]"
    >
      ({{ totalCount }})
    </span>
  </div>
</template>

<script>
import { ref, computed } from 'vue';

export default {
  name: 'StarRating',
  props: {
    modelValue: {
      type: Number,
      default: 0
    },
    rating: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 5
    },
    interactive: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'sm', // 'xs', 'sm', 'md', 'lg', 'xl'
      validator: (v) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v)
    },
    showValue: {
      type: Boolean,
      default: false
    },
    totalCount: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const hoverRating = ref(0);

    const currentRating = computed(() => {
      if (props.interactive) {
        return hoverRating.value > 0 ? hoverRating.value : (props.modelValue || 0);
      }
      return props.rating || props.modelValue || 0;
    });

    const displayRating = computed(() => {
      const val = props.interactive ? props.modelValue : (props.rating || props.modelValue || 0);
      return Number(val).toFixed(1);
    });

    const starSize = computed(() => {
      switch (props.size) {
        case 'xs': return 'w-3 h-3';
        case 'sm': return 'w-4 h-4';
        case 'md': return 'w-5 h-5';
        case 'lg': return 'w-7 h-7';
        case 'xl': return 'w-9 h-9';
        default: return 'w-4 h-4';
      }
    });

    const starSpacing = computed(() => {
      switch (props.size) {
        case 'lg':
        case 'xl': return 'gap-1';
        default: return 'gap-0.5';
      }
    });

    const textSize = computed(() => {
      switch (props.size) {
        case 'xs': return 'text-[11px]';
        case 'sm': return 'text-xs';
        case 'md': return 'text-sm';
        case 'lg': return 'text-base';
        case 'xl': return 'text-xl';
        default: return 'text-xs';
      }
    });

    const countSize = computed(() => {
      switch (props.size) {
        case 'xs': return 'text-[10px]';
        case 'sm': return 'text-xs';
        case 'md': return 'text-xs';
        case 'lg': return 'text-sm';
        case 'xl': return 'text-base';
        default: return 'text-xs';
      }
    });

    const textColor = computed(() => {
      return 'text-amber-500 dark:text-amber-400';
    });

    const getStarColor = (starIndex) => {
      const activeValue = currentRating.value;
      if (starIndex <= activeValue) {
        return 'text-amber-400 fill-amber-400 drop-shadow-sm';
      }
      if (starIndex - 0.5 <= activeValue) {
        return 'text-amber-400/80 fill-amber-400/50';
      }
      return 'text-slate-200 dark:text-slate-700 fill-slate-200/50 dark:fill-slate-800';
    };

    const onHover = (index) => {
      if (props.interactive) {
        hoverRating.value = index;
      }
    };

    const onLeave = () => {
      if (props.interactive) {
        hoverRating.value = 0;
      }
    };

    const onClick = (index) => {
      if (props.interactive) {
        emit('update:modelValue', index);
        emit('change', index);
      }
    };

    return {
      currentRating,
      displayRating,
      starSize,
      starSpacing,
      textSize,
      countSize,
      textColor,
      getStarColor,
      onHover,
      onLeave,
      onClick
    };
  }
};
</script>
